import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Pause, Play, ArrowLeft, Trash2, ThumbsUp, EyeClosed, Snowflake } from 'lucide-react';
import { useLandscapeOverlay } from '@/hooks/useLandscapeOverlay';

// Long press duration in milliseconds
const LONG_PRESS_DURATION = 400;

interface CameraColorPickerProps {
  onComplete: (colors: string[]) => void;
  onCancel: (buttonRect?: DOMRect) => void;
  showOpeningShutter?: boolean;
}

export function CameraColorPicker({ onComplete, onCancel, showOpeningShutter = false }: CameraColorPickerProps) {
  const [selectedColors, setSelectedColors] = useState<string[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [hasCamera, setHasCamera] = useState(true);
  const [isFrozen, setIsFrozen] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [flashColor, setFlashColor] = useState<string | null>(null);
  const [isBlinking, setIsBlinking] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [shutterOpen, setShutterOpen] = useState(!showOpeningShutter);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageCanvasRef = useRef<HTMLCanvasElement>(null);
  const mediaContainerRef = useRef<HTMLDivElement>(null);
  const backButtonRef = useRef<HTMLButtonElement>(null);
  const completeButtonRef = useRef<HTMLButtonElement>(null);
  const lastPinchDistance = useRef<number | null>(null);
  const lastPanPosition = useRef<{ x: number; y: number } | null>(null);
  const isPanning = useRef(false);
  const panDistanceMoved = useRef(0);
  const wasPinching = useRef(false);
  
  // Drag reorder state
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragPosition, setDragPosition] = useState<{ x: number; y: number } | null>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dragStartPos = useRef<{ x: number; y: number } | null>(null);
  const longPressTriggered = useRef(false);
  const colorCirclesContainerRef = useRef<HTMLDivElement>(null);
  
  // Snowflake burst particles for freeze animation
  const [snowflakes, setSnowflakes] = useState<Array<{
    id: number;
    emoji: string;
    angle: number;
    distance: number;
    size: number;
    delay: number;
    rotation: number;
  }>>([]);
  const [freezeAnimKey, setFreezeAnimKey] = useState(0);

  // Ring dimensions for color circle positioning
  const ringSize = 280;
  const colorCircleRadius = ringSize / 2 + 48;
  const circleButtonSize = 56; // w-14 = 56px

  // Suppress landscape overlay while camera is active
  const { suppress, unsuppress } = useLandscapeOverlay();
  
  useEffect(() => {
    suppress();
    return () => {
      // Just unsuppress - let the destination handle showing rotate prompt if needed
      unsuppress();
    };
  }, [suppress, unsuppress]);

  // Track window dimensions for responsive layout
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setWindowHeight(window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Trigger opening shutter animation only after camera stream is ready
  useEffect(() => {
    if (showOpeningShutter && !shutterOpen && stream) {
      // Camera is ready, open the shutter
      const timer = setTimeout(() => {
        setShutterOpen(true);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [showOpeningShutter, shutterOpen, stream]);

  // Detect landscape mode
  const isLandscape = windowWidth > windowHeight;

  // Handle pinch to zoom and single-finger pan when frozen
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 1 && isFrozen) {
      // Start panning
      isPanning.current = true;
      panDistanceMoved.current = 0;
      lastPanPosition.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
    }
  }, [isFrozen]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      // Pinch to zoom
      e.preventDefault();
      isPanning.current = false;
      wasPinching.current = true;
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );

      if (lastPinchDistance.current !== null) {
        const delta = distance - lastPinchDistance.current;
        // Zoom speed scales with current level so higher magnification feels responsive
        setZoomLevel((prev) => {
          const speed = 0.01 * Math.max(1, prev * 0.5);
          return Math.max(1, Math.min(10, prev + delta * speed));
        });
      }
      lastPinchDistance.current = distance;
    } else if (e.touches.length === 1 && isFrozen && isPanning.current && lastPanPosition.current) {
      // Single finger pan when frozen
      const deltaX = e.touches[0].clientX - lastPanPosition.current.x;
      const deltaY = e.touches[0].clientY - lastPanPosition.current.y;
      
      // Track total distance moved for tap vs pan detection
      panDistanceMoved.current += Math.abs(deltaX) + Math.abs(deltaY);
      
      // Calculate max pan based on zoom level (more zoom = more pan allowed)
      const maxPan = (zoomLevel - 1) * 150;
      
      setPanOffset((prev) => ({
        x: Math.max(-maxPan, Math.min(maxPan, prev.x + deltaX)),
        y: Math.max(-maxPan, Math.min(maxPan, prev.y + deltaY)),
      }));
      
      lastPanPosition.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
    }
  }, [isFrozen, zoomLevel]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    lastPinchDistance.current = null;
    lastPanPosition.current = null;
    isPanning.current = false;
    // Only clear pinch flag once all fingers have lifted
    if (e.touches.length === 0) {
      // Use a microtask so the flag survives the touchend→handleTap call in the same tick
      setTimeout(() => { wasPinching.current = false; }, 0);
    }
  }, []);

  // Clear flash after animation
  useEffect(() => {
    if (flashColor) {
      const timer = setTimeout(() => setFlashColor(null), 2000);
      return () => clearTimeout(timer);
    }
  }, [flashColor]);

  // Clear blink after animation
  useEffect(() => {
    if (isBlinking) {
      const timer = setTimeout(() => setIsBlinking(false), 600);
      return () => clearTimeout(timer);
    }
  }, [isBlinking]);

  useEffect(() => {
    let isMounted = true;
    
    const startCamera = async () => {
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
        });
        
        if (isMounted) {
          setStream(mediaStream);
          if (videoRef.current) {
            videoRef.current.srcObject = mediaStream;
          }
        }
      } catch (err) {
        console.error('Camera access denied:', err);
        if (isMounted) {
          setHasCamera(false);
        }
      }
    };

    startCamera();

    return () => {
      isMounted = false;
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [stream]);

  const handleCapture = () => {
    if (!videoRef.current || !canvasRef.current || !imageCanvasRef.current) return;
    if (navigator.vibrate) navigator.vibrate([10, 30, 10]);
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) return;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);
    
    // Also draw to imageCanvasRef so it's ready for frozen tap color sampling
    const imgCanvas = imageCanvasRef.current;
    const imgCtx = imgCanvas.getContext('2d');
    if (imgCtx) {
      imgCanvas.width = video.videoWidth;
      imgCanvas.height = video.videoHeight;
      imgCtx.drawImage(video, 0, 0);
    }
    
    const imageData = canvas.toDataURL('image/jpeg');
    setCapturedImage(imageData);
    setIsFrozen(true);
    
    // Generate snowflake burst particles
    const snowflakeEmojis = ['❄️', '❅', '❆', '✦', '✧', '·'];
    const particles = Array.from({ length: 20 }, (_, i) => ({
      id: i,
      emoji: snowflakeEmojis[Math.floor(Math.random() * snowflakeEmojis.length)],
      angle: (360 / 20) * i + (Math.random() * 30 - 15),
      distance: 60 + Math.random() * 100,
      size: 10 + Math.random() * 16,
      delay: Math.random() * 0.15,
      rotation: Math.random() * 540 - 270,
    }));
    setSnowflakes(particles);
    setFreezeAnimKey((k) => k + 1);
    
    // Clear particles after animation completes
    setTimeout(() => setSnowflakes([]), 1200);
    // Don't disable tracks - just overlay the captured image
  };

  const handleRetake = () => {
    setCapturedImage(null);
    setIsFrozen(false);
    setPanOffset({ x: 0, y: 0 }); // Reset pan when unfreezing
    // Keep selected colors - don't wipe them when resuming live view
    // Video keeps playing underneath, just show it again
  };

  // Store current values in refs so getColorAtPoint always reads the
  // latest state, eliminating any possible stale-closure issues.
  const zoomRef = useRef(zoomLevel);
  const panRef = useRef(panOffset);
  const frozenRef = useRef(isFrozen);
  zoomRef.current = zoomLevel;
  panRef.current = panOffset;
  frozenRef.current = isFrozen;

  const getColorAtPoint = useCallback((clientX: number, clientY: number): string | null => {
    const container = mediaContainerRef.current;
    if (!container) return null;

    const frozen = frozenRef.current;
    const zoom = zoomRef.current;
    const pan = panRef.current;

    const sourceCanvas = frozen ? imageCanvasRef.current : canvasRef.current;
    if (!sourceCanvas) return null;

    const srcCtx = sourceCanvas.getContext('2d');
    if (!srcCtx) return null;

    const srcW = sourceCanvas.width;
    const srcH = sourceCanvas.height;
    if (srcW === 0 || srcH === 0) return null;

    const containerRect = container.getBoundingClientRect();
    const viewW = containerRect.width;
    const viewH = containerRect.height;

    // Tap position relative to container top-left
    const screenX = clientX - containerRect.left;
    const screenY = clientY - containerRect.top;

    // Instead of trying to invert the CSS transform, draw the source image
    // onto a scratch canvas using the exact same transform the CSS applies,
    // then read the pixel at the tap position directly. This is robust against
    // any transform complexity (zoom, pan, object-cover).
    const scratch = document.createElement('canvas');
    scratch.width = viewW;
    scratch.height = viewH;
    const ctx = scratch.getContext('2d');
    if (!ctx) return null;

    // Replicate object-cover: scale to fill, center the overflow
    const coverScale = Math.max(viewW / srcW, viewH / srcH);
    const renderedW = srcW * coverScale;
    const renderedH = srcH * coverScale;
    const offsetX = (viewW - renderedW) / 2;
    const offsetY = (viewH - renderedH) / 2;

    // Apply the same CSS transform: scale(zoom) translate(panX/zoom, panY/zoom)
    // with transform-origin center
    const panX = frozen ? pan.x : 0;
    const panY = frozen ? pan.y : 0;
    const cx = viewW / 2;
    const cy = viewH / 2;

    ctx.translate(cx, cy);
    ctx.scale(zoom, zoom);
    ctx.translate(panX / zoom, panY / zoom);
    ctx.translate(-cx, -cy);

    ctx.drawImage(sourceCanvas, offsetX, offsetY, renderedW, renderedH);

    // Read the pixel directly at tap position - no inverse math needed
    const px = Math.max(0, Math.min(scratch.width - 1, Math.round(screenX)));
    const py = Math.max(0, Math.min(scratch.height - 1, Math.round(screenY)));
    const pixel = ctx.getImageData(px, py, 1, 1).data;

    // If the pixel is fully transparent we tapped outside the image
    if (pixel[3] === 0) return null;

    const hex = '#' + [pixel[0], pixel[1], pixel[2]]
      .map((v) => v.toString(16).padStart(2, '0'))
      .join('');

    return hex.toUpperCase();
  }, []);

  const addColor = (color: string) => {
    if (selectedColors.length < 6 && !selectedColors.includes(color)) {
      if (navigator.vibrate) navigator.vibrate(15);
      const newColors = [...selectedColors, color];
      setSelectedColors(newColors);
      setSelectedIndex(newColors.length - 1); // Select newly added color
      setFlashColor(color); // Trigger flash effect
      setIsBlinking(true); // Trigger blink effect
    }
  };

  const handleTap = (e: React.PointerEvent<HTMLDivElement>) => {
    if (selectedColors.length >= 6) return;
    
    // Only respond to primary pointer (left-click / single finger)
    if (!e.isPrimary) return;
    
    // If we just finished panning (moved more than 10px), don't treat as tap
    if (e.pointerType === 'touch' && panDistanceMoved.current > 10) {
      panDistanceMoved.current = 0;
      return;
    }
    
    // Suppress taps that fire as a side-effect of lifting a finger after pinch zoom
    if (e.pointerType === 'touch' && wasPinching.current) {
      return;
    }
    
    const clientX = e.clientX;
    const clientY = e.clientY;
    
    if (!isFrozen && videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      if (ctx) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0);
      }
    }
    
    // imageCanvasRef is pre-populated at capture time, so no async loading needed
    const color = getColorAtPoint(clientX, clientY);
    if (color) {
      addColor(color);
    }
  };

  const handleRemoveColor = (index: number) => {
    const newColors = selectedColors.filter((_, i) => i !== index);
    setSelectedColors(newColors);
    // Select the previous circle, or null if we deleted the first one
    if (index > 0) {
      setSelectedIndex(index - 1);
    } else if (newColors.length > 0) {
      setSelectedIndex(0);
    } else {
      setSelectedIndex(null);
    }
  };

  const handleSelectColor = (index: number) => {
    setSelectedIndex(selectedIndex === index ? null : index);
  };

  const handleClearSelected = () => {
    if (selectedIndex !== null) {
      handleRemoveColor(selectedIndex);
    }
  };

  // Calculate positions for color circles
  // In portrait: fan around top of circle
  // In landscape: 3 on left side, 3 on right side
  const getColorPosition = useCallback((index: number, total: number) => {
    if (isLandscape) {
      // Split colors to left and right sides
      const leftCount = Math.ceil(total / 2);
      const isLeft = index < leftCount;
      const sideIndex = isLeft ? index : index - leftCount;
      const sideTotal = isLeft ? leftCount : total - leftCount;
      
      // Vertical spacing on each side
      const spacing = 70; // pixels between circles
      const startY = -((sideTotal - 1) * spacing) / 2;
      const y = startY + sideIndex * spacing;
      
      // X position: outside the ring
      const x = isLeft ? -(ringSize / 2 + 50) : (ringSize / 2 + 50);
      
      return { x, y };
    } else {
      // Portrait: fan around top
      const maxX = windowWidth / 2 - circleButtonSize / 2 - 16;
      const maxSin = Math.min(1, maxX / colorCircleRadius);
      const maxHalfSpread = Math.asin(maxSin) * (180 / Math.PI);
      const maxSpreadAngle = Math.min(150, maxHalfSpread * 2);
      
      const spreadAngle = total === 1 ? 0 : ((total - 1) / 5) * maxSpreadAngle;
      const startAngle = -90 - spreadAngle / 2;
      const angleStep = total > 1 ? spreadAngle / (total - 1) : 0;
      const angle = total === 1 ? -90 : startAngle + (index * angleStep);
      const rad = (angle * Math.PI) / 180;
      
      return {
        x: Math.cos(rad) * colorCircleRadius,
        y: Math.sin(rad) * colorCircleRadius,
      };
    }
  }, [isLandscape, windowWidth, colorCircleRadius, circleButtonSize, ringSize]);

  // Cleanup long press timer on unmount
  useEffect(() => {
    return () => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
      }
    };
  }, []);

  // Handle long press start for drag reorder
  const handleColorPointerDown = useCallback((e: React.PointerEvent, index: number) => {
    // Only handle filled color circles
    if (index >= selectedColors.length) return;
    
    const startX = e.clientX;
    const startY = e.clientY;
    dragStartPos.current = { x: startX, y: startY };
    longPressTriggered.current = false;
    
    // Capture pointer to track moves outside element
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    
    // Start long press timer
    longPressTimer.current = setTimeout(() => {
      longPressTriggered.current = true;
      // Start dragging
      setDragIndex(index);
      setDragPosition({ x: startX, y: startY });
      // Haptic feedback if available
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
    }, LONG_PRESS_DURATION);
  }, [selectedColors.length]);

  // Handle pointer move during drag
  const handleColorPointerMove = useCallback((e: React.PointerEvent) => {
    // If we're in drag mode, update position
    if (dragIndex !== null) {
      setDragPosition({ x: e.clientX, y: e.clientY });
      return;
    }
    
    // Cancel long press if moved too far before it triggered
    if (dragStartPos.current && longPressTimer.current) {
      const dx = e.clientX - dragStartPos.current.x;
      const dy = e.clientY - dragStartPos.current.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance > 10) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
    }
  }, [dragIndex]);

  // Find which index the dragged item should be dropped at
  const getDropIndex = useCallback((dragX: number, dragY: number): number => {
    if (dragIndex === null) return -1;
    
    const colorCount = selectedColors.length;
    let closestIndex = dragIndex;
    let closestDistance = Infinity;
    
    // Get the center of the color circles container (the circular view area)
    const container = colorCirclesContainerRef.current;
    if (!container) return dragIndex;
    
    const containerRect = container.getBoundingClientRect();
    const centerX = containerRect.left + containerRect.width / 2;
    const centerY = containerRect.top + containerRect.height / 2;
    
    for (let i = 0; i < colorCount; i++) {
      if (i === dragIndex) continue;
      
      const pos = getColorPosition(i, Math.min(colorCount + 1, 6));
      const circleX = centerX + pos.x;
      const circleY = centerY + pos.y;
      
      const distance = Math.sqrt(
        Math.pow(dragX - circleX, 2) + Math.pow(dragY - circleY, 2)
      );
      
      if (distance < closestDistance && distance < 80) {
        closestDistance = distance;
        closestIndex = i;
      }
    }
    
    return closestIndex;
  }, [dragIndex, selectedColors.length, getColorPosition]);

  // Calculate drop index during drag
  const dropIndex = useMemo(() => {
    if (dragIndex === null || !dragPosition) return null;
    return getDropIndex(dragPosition.x, dragPosition.y);
  }, [dragIndex, dragPosition, getDropIndex]);

  // Handle actual reordering when drag ends
  const handleDragEnd = useCallback(() => {
    if (dragIndex !== null && dropIndex !== null && dragIndex !== dropIndex) {
      const newColors = [...selectedColors];
      const [removed] = newColors.splice(dragIndex, 1);
      newColors.splice(dropIndex, 0, removed);
      setSelectedColors(newColors);
      
      // Update selected index to follow the dragged item
      if (selectedIndex === dragIndex) {
        setSelectedIndex(dropIndex);
      } else if (selectedIndex !== null) {
        // Adjust selected index if it was affected by the move
        if (dragIndex < selectedIndex && dropIndex >= selectedIndex) {
          setSelectedIndex(selectedIndex - 1);
        } else if (dragIndex > selectedIndex && dropIndex <= selectedIndex) {
          setSelectedIndex(selectedIndex + 1);
        }
      }
    }
    
    setDragIndex(null);
    setDragPosition(null);
  }, [dragIndex, dropIndex, selectedColors, selectedIndex]);

  // Handle pointer up with reordering
  const handleColorPointerUpWithReorder = useCallback(() => {
    // Clear long press timer
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    dragStartPos.current = null;
    
    // Handle reorder if drag was in progress
    if (dragIndex !== null) {
      handleDragEnd();
    }
  }, [dragIndex, handleDragEnd]);

  const handleComplete = () => {
    if (selectedColors.length >= 3) {
      // Stop camera and pass colors to parent (parent handles the curtain transition)
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
      onComplete(selectedColors);
    }
  };

  const handleCancel = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
    const rect = backButtonRef.current?.getBoundingClientRect();
    onCancel(rect);
  };

  const canComplete = selectedColors.length >= 3 && selectedColors.length <= 6;

  // Calculate responsive button offset for both landscape and portrait modes
  // On small screens, position buttons closer to the ring to stay within viewport
  // This hook must be called unconditionally before the early return
  const buttonOffsets = useMemo(() => {
    // Button size: w-14 (56px) on small screens, w-16 (64px) on sm and up
    // For safety calculations, use smaller size
    const buttonSize = windowWidth < 640 ? 56 : 64;
    const safeMargin = 8; // Minimum margin from edge
    
    if (isLandscape) {
      // Landscape: buttons on left and right of ring
      // The button uses translateX(-50%) or translateX(50%), so it extends
      // half its width beyond the offset point. We need to ensure the full
      // button stays within the viewport.
      const availableHalf = windowWidth / 2;
      // Account for safe-area-inset on sides (approximate 44px for iPhone notch area)
      const safeAreaInset = 44;
      // Button is centered on the offset point, so full width must fit
      const buttonSpace = buttonSize + safeMargin + safeAreaInset;
      const idealOffset = ringSize / 2 + 48;
      const maxOffset = availableHalf - buttonSpace;
      return {
        landscape: Math.min(idealOffset, maxOffset),
        portrait: { bottom: 0, horizontal: 0 }, // Not used in landscape
      };
    } else {
      // Portrait: buttons below the ring, left and right sides
      // Ring is centered, buttons are at bottom-left and bottom-right of ring
      const ringBottom = (windowHeight / 2) + (ringSize / 2); // Bottom edge of ring from top
      const availableBelow = windowHeight - ringBottom;
      
      // iPhone SE has ~568px height in portrait, ring center at 284px, ring bottom at ~424px
      // Available space below ring: ~144px
      // We want buttons to be visible with some padding from bottom
      
      // Ideal positioning: offset below ring edge
      // For small screens, use smaller offsets
      const isSmallScreen = windowHeight < 600;
      const idealBottom = isSmallScreen ? -20 : -40;
      const idealHorizontal = isSmallScreen ? -12 : -24;
      
      // Ensure button stays within viewport (accounting for bottom safe area)
      // Button height + safe area margin from bottom of ring position
      const minBottom = Math.max(idealBottom, -(availableBelow - buttonSize - 40));
      
      // Ensure horizontal positioning keeps button on screen
      // Available space from ring edge to screen edge
      const sideSpace = (windowWidth - ringSize) / 2;
      const maxHorizontal = sideSpace - buttonSize / 2 - safeMargin;
      const clampedHorizontal = Math.max(idealHorizontal, -maxHorizontal);
      
      return {
        landscape: 0, // Not used in portrait
        portrait: { bottom: minBottom, horizontal: clampedHorizontal },
      };
    }
  }, [isLandscape, windowWidth, windowHeight, ringSize]);

  const remaining = Math.max(0, 3 - selectedColors.length);

  // If camera is not available, go back to selection (after a brief delay to avoid React state update conflicts)
  useEffect(() => {
    if (!hasCamera) {
      const timer = setTimeout(() => {
        onCancel(undefined);
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [hasCamera, onCancel]);

  // Show a minimal loading state while camera is unavailable (effect will navigate back)
  if (!hasCamera) {
    return (
      <div className="fixed inset-0 z-50 bg-black flex items-center justify-center">
        <div className="text-white/60 text-sm">Camera not available</div>
      </div>
    );
  }

  return (
    <div 
      className="fixed inset-0 z-50 touch-none bg-black"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Full-screen camera feed background */}
      <div ref={mediaContainerRef} className="absolute inset-0 overflow-hidden">
        {/* Video always in DOM to preserve stream */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="w-full h-full object-cover transition-transform"
          style={{ 
            transform: `scale(${zoomLevel})`,
            display: isFrozen ? 'none' : 'block',
          }}
        />
        {/* Captured image overlay when frozen */}
        {isFrozen && capturedImage && (
          <img
            src={capturedImage}
            alt="Captured"
            className="w-full h-full object-cover transition-transform duration-100"
            style={{ transform: `scale(${zoomLevel}) translate(${panOffset.x / zoomLevel}px, ${panOffset.y / zoomLevel}px)` }}
            data-captured-image
          />
        )}
      </div>

      {/* Semi-transparent overlay with circular cutout */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(circle ${ringSize / 2}px at 50% 50%, transparent 0%, transparent 100%), 
                       linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7))`,
          maskImage: `radial-gradient(circle ${ringSize / 2}px at 50% 50%, transparent 0%, transparent 99%, black 100%)`,
          WebkitMaskImage: `radial-gradient(circle ${ringSize / 2}px at 50% 50%, transparent 0%, transparent 99%, black 100%)`,
        }}
      />

      {/* Flash effect when color is added */}
      {flashColor && (
        <div 
          className="absolute inset-0 pointer-events-none flex items-center justify-center overflow-hidden"
          style={{ zIndex: 5 }}
        >
          <div 
            className="aspect-square w-[100vmin] rounded-full animate-color-wave"
            style={{
              background: `radial-gradient(circle, ${flashColor}aa 0%, ${flashColor}66 30%, ${flashColor}33 50%, ${flashColor}11 70%, transparent 85%)`,
            }}
          />
        </div>
      )}

      {/* Back button - top left with safe area */}
      <div 
        className="absolute z-10 top-0 left-0 px-3 py-2"
        style={{
          paddingTop: 'max(env(safe-area-inset-top, 8px), 8px)',
          paddingLeft: 'max(env(safe-area-inset-left, 12px), 12px)',
        }}
      >
        <button
          ref={backButtonRef}
          onClick={handleCancel}
          className="p-2 rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
      </div>

      {/* Text label - bottom left with safe area */}
      <div 
        className="absolute z-10 bottom-0 left-0"
        style={{
          paddingBottom: 'max(env(safe-area-inset-bottom, 16px), 16px)',
          paddingLeft: 'max(env(safe-area-inset-left, 16px), 16px)',
        }}
      >
        <h2 className="text-lg font-semibold text-white">Capture colors</h2>
        <p className="text-xs text-white/60">
          {remaining > 0 ? `${remaining} more needed` : 'Ready!'}
        </p>
      </div>

      {/* FREEZE! overlay when paused */}
      {isFrozen && (
        <div className="absolute top-16 left-0 right-0 z-10 flex justify-center pointer-events-none">
          {/* Snowflake burst particles */}
          {snowflakes.map((flake) => (
            <span
              key={`${freezeAnimKey}-${flake.id}`}
              className="absolute animate-snowflake-burst"
              style={{
                fontSize: `${flake.size}px`,
                '--burst-x': `${Math.cos((flake.angle * Math.PI) / 180) * flake.distance}px`,
                '--burst-y': `${Math.sin((flake.angle * Math.PI) / 180) * flake.distance}px`,
                '--burst-rotation': `${flake.rotation}deg`,
                animationDelay: `${flake.delay}s`,
              } as React.CSSProperties}
            >
              {flake.emoji}
            </span>
          ))}
          {/* Badge with pop-in */}
          <div 
            key={freezeAnimKey}
            className="bg-sky-500/90 backdrop-blur-sm px-4 py-1.5 rounded-full flex items-center gap-2 animate-freeze-pop-in"
          >
            <Snowflake className="h-4 w-4 text-white/80" />
            <span className="text-white font-bold tracking-wider text-sm">FREEZE!</span>
          </div>
        </div>
      )}

      {/* Camera View - circular interaction area */}
      <div className="absolute inset-0 flex items-center justify-center">
        {/* Circular camera view container */}
        <div 
          className="relative"
          style={{ width: ringSize, height: ringSize }}
        >
          {/* Invisible tap area matching the circle */}
          <div
            className="absolute inset-0 rounded-full cursor-pointer ring-4 ring-white/20"
            onPointerUp={handleTap}
          >
            {/* Tap instruction overlay */}
            {selectedColors.length === 0 && !isBlinking && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="bg-black/40 backdrop-blur-sm rounded-2xl px-6 py-4 text-white text-center">
                  <p className="text-2xl mb-1">👁️</p>
                  <p className="text-sm">Tap colors</p>
                </div>
              </div>
            )}

            {/* Eyelid blink animation */}
            {isBlinking && flashColor && (
              <div className="absolute inset-0 rounded-full pointer-events-none overflow-hidden">
                <div
                  className="absolute left-0 right-0 animate-eyelid-blink"
                  style={{
                    top: 0,
                    width: '100%',
                    height: '100%',
                  }}
                >
                  {/* The circular eyelid */}
                  <div
                    className="absolute rounded-full"
                    style={{
                      width: '100%',
                      height: '100%',
                      backgroundColor: flashColor,
                      bottom: 0,
                      left: 0,
                    }}
                  />
                  {/* EyeClosed icon on the eyelid */}
                  <EyeClosed 
                    className="absolute w-full h-auto"
                    style={{ 
                      color: 'rgba(0,0,0,0.3)',
                      top: '100%',
                      transform: 'translateY(-60%)',
                    }}
                    strokeWidth={1.5}
                  />
                </div>
              </div>
            )}
          </div>

          <canvas ref={canvasRef} className="hidden" />
          <canvas ref={imageCanvasRef} className="hidden" />

          {/* Selected colors - fanned around top (portrait) or sides (landscape) */}
          <div
            ref={colorCirclesContainerRef}
            onPointerMove={handleColorPointerMove}
            onPointerUp={handleColorPointerUpWithReorder}
            onPointerCancel={handleColorPointerUpWithReorder}
            onPointerLeave={handleColorPointerUpWithReorder}
            className="absolute inset-0"
            style={{ pointerEvents: dragIndex !== null ? 'auto' : 'none' }}
          >
            {(() => {
              const maxColors = 6;
              const circleCount = Math.min(selectedColors.length + 1, maxColors);
              
              return Array.from({ length: circleCount }).map((_, i) => {
                const pos = getColorPosition(i, circleCount);
                const hasColor = i < selectedColors.length;
                const color = selectedColors[i];
                const isSelected = selectedIndex === i;
                const isDragged = dragIndex === i;
                const isDropTarget = dropIndex === i && dragIndex !== null && dragIndex !== i;
                
                // Calculate position for dragged item
                let displayPos = pos;
                if (isDragged && dragPosition && colorCirclesContainerRef.current) {
                  const containerRect = colorCirclesContainerRef.current.getBoundingClientRect();
                  const centerX = containerRect.left + containerRect.width / 2;
                  const centerY = containerRect.top + containerRect.height / 2;
                  displayPos = {
                    x: dragPosition.x - centerX,
                    y: dragPosition.y - centerY,
                  };
                }
                
                return (
                  <div
                    key={i}
                    className={`absolute transition-all ${isDragged ? 'duration-0 z-50' : 'duration-300'} ${isDropTarget ? 'scale-75 opacity-50' : ''}`}
                    style={{
                      left: `calc(50% + ${displayPos.x}px - 28px)`,
                      top: `calc(50% + ${displayPos.y}px - 28px)`,
                    }}
                  >
                    {hasColor ? (
                      <button
                        onPointerDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleColorPointerDown(e, i);
                        }}
                        onPointerMove={handleColorPointerMove}
                        onPointerUp={() => {
                          // Clear timer first
                          if (longPressTimer.current) {
                            clearTimeout(longPressTimer.current);
                            longPressTimer.current = null;
                          }
                          
                          // Handle click only if long press wasn't triggered and not dragging
                          if (!longPressTriggered.current && dragIndex === null) {
                            handleSelectColor(i);
                          }
                          
                          handleColorPointerUpWithReorder();
                          longPressTriggered.current = false;
                        }}
                        onPointerCancel={() => {
                          if (longPressTimer.current) {
                            clearTimeout(longPressTimer.current);
                            longPressTimer.current = null;
                          }
                          handleColorPointerUpWithReorder();
                          longPressTriggered.current = false;
                        }}
                        className={`w-14 h-14 rounded-full shadow-md transition-all touch-none select-none ${
                          isDragged 
                            ? 'scale-125 shadow-2xl cursor-grabbing' 
                            : 'hover:scale-110 active:scale-95 cursor-grab'
                        } ${
                          isSelected && !isDragged ? 'ring-4 shadow-xl scale-110' : 'ring-2 ring-white/30'
                        }`}
                        style={{ 
                          backgroundColor: color,
                          ['--tw-ring-color' as string]: isSelected ? color : undefined,
                          pointerEvents: 'auto',
                        }}
                      />
                    ) : (
                      <div 
                        className="w-14 h-14 rounded-full border-2 border-dashed border-white/30"
                      />
                    )}
                  </div>
                );
              });
            })()}
          </div>

          {/* Clear button - portrait only (inside ring) */}
          {!isLandscape && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleClearSelected();
              }}
              disabled={selectedIndex === null}
              className="absolute w-14 h-14 sm:w-16 sm:h-16 rounded-full shadow-lg bg-white/20 backdrop-blur-sm hover:bg-white/30 hover:scale-110 active:scale-95 transition-all disabled:opacity-30 disabled:hover:scale-100 flex items-center justify-center"
              style={{ 
                left: buttonOffsets.portrait.horizontal,
                bottom: buttonOffsets.portrait.bottom,
              }}
              title="Remove selected color"
            >
              <Trash2 className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </button>
          )}

          {/* Freeze/Resume button - portrait only (inside ring) */}
          {!isLandscape && (
            !isFrozen ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCapture();
                }}
                className="absolute w-14 h-14 sm:w-16 sm:h-16 rounded-full shadow-lg bg-gradient-to-r from-sky-400 to-violet-500 hover:scale-110 active:scale-95 transition-all flex items-center justify-center"
                style={{ 
                  right: buttonOffsets.portrait.horizontal,
                  bottom: buttonOffsets.portrait.bottom,
                }}
                title="Freeze frame"
              >
                <Pause className="h-5 w-5 sm:h-6 sm:w-6 text-white" fill="white" />
              </button>
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleRetake();
                }}
                className="absolute w-14 h-14 sm:w-16 sm:h-16 rounded-full shadow-lg bg-gradient-to-r from-emerald-400 to-sky-400 hover:scale-110 active:scale-95 transition-all flex items-center justify-center"
                style={{ 
                  right: buttonOffsets.portrait.horizontal,
                  bottom: buttonOffsets.portrait.bottom,
                }}
                title="Resume live view"
              >
                <Play className="h-5 w-5 sm:h-6 sm:w-6 text-white" fill="white" />
              </button>
            )
          )}
        </div>
      </div>

      {/* Landscape mode buttons - positioned at viewport edges */}
      {isLandscape && (
        <>
          {/* Clear button - left edge */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleClearSelected();
            }}
            disabled={selectedIndex === null}
            className="absolute z-10 w-14 h-14 sm:w-16 sm:h-16 rounded-full shadow-lg bg-white/20 backdrop-blur-sm hover:bg-white/30 hover:scale-110 active:scale-95 transition-all disabled:opacity-30 disabled:hover:scale-100 flex items-center justify-center"
            style={{ 
              left: `max(env(safe-area-inset-left, 8px), 8px)`,
              top: '50%',
              transform: 'translateY(-50%)',
            }}
            title="Remove selected color"
          >
            <Trash2 className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
          </button>

          {/* Freeze/Resume button - right edge */}
          {!isFrozen ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCapture();
              }}
              className="absolute z-10 w-14 h-14 sm:w-16 sm:h-16 rounded-full shadow-lg bg-gradient-to-r from-sky-400 to-violet-500 hover:scale-110 active:scale-95 transition-all flex items-center justify-center"
              style={{ 
                right: `max(env(safe-area-inset-right, 8px), 8px)`,
                top: '50%',
                transform: 'translateY(-50%)',
              }}
              title="Freeze frame"
            >
              <Pause className="h-5 w-5 sm:h-6 sm:w-6 text-white" fill="white" />
            </button>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRetake();
              }}
              className="absolute z-10 w-14 h-14 sm:w-16 sm:h-16 rounded-full shadow-lg bg-gradient-to-r from-emerald-400 to-sky-400 hover:scale-110 active:scale-95 transition-all flex items-center justify-center"
              style={{ 
                right: `max(env(safe-area-inset-right, 8px), 8px)`,
                top: '50%',
                transform: 'translateY(-50%)',
              }}
              title="Resume live view"
            >
              <Play className="h-5 w-5 sm:h-6 sm:w-6 text-white" fill="white" />
            </button>
          )}
        </>
      )}

      {/* Continue button - bottom right corner with safe area */}
      <div 
        className="absolute z-10 bottom-0 right-0"
        style={{
          paddingBottom: 'max(env(safe-area-inset-bottom, 16px), 16px)',
          paddingRight: 'max(env(safe-area-inset-right, 16px), 16px)',
        }}
      >
        <div className="relative">
          {/* Wavy shine effect when 3+ colors selected */}
          {canComplete && (
            <>
              <div className="absolute inset-0 rounded-full animate-wavy-shine-1">
                <div className="absolute inset-[-4px] rounded-full bg-gradient-to-r from-rose-400/60 via-amber-300/60 to-emerald-400/60 blur-md" />
              </div>
              <div className="absolute inset-0 rounded-full animate-wavy-shine-2">
                <div className="absolute inset-[-6px] rounded-full bg-gradient-to-r from-sky-400/40 via-violet-400/40 to-rose-400/40 blur-lg" />
              </div>
              <div className="absolute inset-0 rounded-full animate-wavy-shine-3">
                <div className="absolute inset-[-8px] rounded-full bg-gradient-to-r from-amber-300/30 via-emerald-400/30 to-sky-400/30 blur-xl" />
              </div>
            </>
          )}
          <button
            ref={completeButtonRef}
            onClick={handleComplete}
            disabled={!canComplete}
            className={`relative rounded-full bg-gradient-to-r from-rose-400 via-amber-400 to-emerald-400 text-white shadow-lg hover:scale-110 active:scale-95 transition-all duration-300 disabled:opacity-40 disabled:hover:scale-100 flex items-center justify-center ${
              canComplete ? 'w-16 h-16' : 'w-14 h-14'
            }`}
            title="Continue"
          >
            <ThumbsUp className={`transition-all duration-300 ${canComplete ? 'h-7 w-7' : 'h-6 w-6'}`} />
          </button>
        </div>
      </div>

      {/* Opening shutter animation - simplified for performance */}
      {showOpeningShutter && (
        <div 
          className="fixed inset-0 z-[60] pointer-events-none overflow-hidden"
        >
          {isLandscape ? (
            <>
              {/* Left bar - simplified solid color */}
              <div 
                className="absolute top-0 bottom-0 left-0 bg-zinc-800 will-change-transform"
                style={{
                  width: 'calc(50% + 2px)',
                  transform: shutterOpen ? 'translateX(-100%)' : 'translateX(0)',
                  transitionProperty: 'transform',
                  transitionDuration: '200ms',
                  transitionTimingFunction: 'ease-out',
                }}
              />
              
              {/* Right bar - simplified solid color */}
              <div 
                className="absolute top-0 bottom-0 right-0 bg-zinc-800 will-change-transform"
                style={{
                  width: 'calc(50% + 2px)',
                  transform: shutterOpen ? 'translateX(100%)' : 'translateX(0)',
                  transitionProperty: 'transform',
                  transitionDuration: '200ms',
                  transitionTimingFunction: 'ease-out',
                }}
              />
            </>
          ) : (
            <>
              {/* Top bar - simplified solid color */}
              <div 
                className="absolute top-0 left-0 right-0 bg-zinc-800 will-change-transform"
                style={{
                  height: 'calc(50% + 2px)',
                  transform: shutterOpen ? 'translateY(-100%)' : 'translateY(0)',
                  transitionProperty: 'transform',
                  transitionDuration: '200ms',
                  transitionTimingFunction: 'ease-out',
                }}
              />
              
              {/* Bottom bar - simplified solid color */}
              <div 
                className="absolute bottom-0 left-0 right-0 bg-zinc-800 will-change-transform"
                style={{
                  height: 'calc(50% + 2px)',
                  transform: shutterOpen ? 'translateY(100%)' : 'translateY(0)',
                  transitionProperty: 'transform',
                  transitionDuration: '200ms',
                  transitionTimingFunction: 'ease-out',
                }}
              />
            </>
          )}

          {/* Center eye - simplified */}
          <div 
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            style={{
              opacity: shutterOpen ? 0 : 1,
              transitionProperty: 'opacity',
              transitionDuration: '100ms',
            }}
          >
            <div className="relative w-16 h-16">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-rose-300 via-amber-200 to-sky-300" />
              <div className="absolute inset-2 rounded-full bg-white/80" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-3 h-3 rounded-full bg-zinc-800" />
              </div>
            </div>
          </div>
        </div>
      )}


    </div>
  );
}
